import { Api } from './axios-config';
import { IProductView } from './productService';
import { IServiceView } from './serviceService';

export interface IPaymentSplitView {
  paymentMethod: number;
  amount: number;
  paymentMethodName: string;
}

export interface IAppointmentView {
  id: number;
  appointmentNumber: number;
  isWalkIn: boolean;
  date: Date;
  startTime: string;
  endTime: string;
  userId?: number;
  services: IServiceView[];
  products: IProductView[];
  businessEmployeeId: number;
  businessId: number;
  canceled: boolean;
  past: boolean;
  completed: boolean;
  status: number;
  packageDiscount: number;
  totalAmount: number;
  employeeColor: string;
  employeeName: string;
  customerName: string;
  customerPhone?: string;
  predefinedMessages?: string;
  businessName: string;
  businessAddress: string;
  paymentMethod?: number | null;
  paymentSplits?: IPaymentSplitView[];
  notes?: string;
  canceledAt?: string;
  canceledByUserId?: number;
  cancelReason?: string;
}

const getById = async (id: number): Promise<IAppointmentView> => {
  try {
    const { data } = await Api.get<IAppointmentView>(
      `/appointment/${id}`
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const get = async (
  businessId?: number,
  businessEmployeeId?: number,
  startDate?: Date,
  endDate?: Date,
  hasServices?: boolean
): Promise<IAppointmentView[]> => {
  try {
    const queryParams: Record<string, string> = {
      businessId: businessId?.toString() || '',
      businessEmployeeId: businessEmployeeId?.toString() || '',
      startDate: startDate?.toISOString() || '',
      endDate: endDate?.toISOString() || '',
    };

    if (hasServices !== undefined) {
      queryParams.hasServices = String(hasServices);
    }

    const { data } = await Api.get('/appointment', {
      params: queryParams,
    });

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface ICreateAppointment {
  date: Date;
  userId?: number;
  customer?: string;
  services: IAppointmentServiceDto[];
  products: IAppointmentProductDto[];
  businessEmployeeId: number;
  businessId: number;
  isWalkIn?: boolean;
}

const create = async (body: ICreateAppointment): Promise<number> => {
  try {
    const { data } = await Api.post(
      '/appointment',
      body
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface IAppointmentProductDto {
  productId: number;
  soldByBusinessEmployeeId?: number;
}

export interface IAppointmentServiceDto {
  serviceId: number;
  soldByBusinessEmployeeId?: number;
}

export interface ICompleteAppointment {
  id: number;
  services: { serviceId: number; soldByBusinessEmployeeId: number }[];
  products: { productId: number; soldByBusinessEmployeeId: number }[];
  paymentMethod?: number;
  paymentSplits?: { paymentMethod: number; amount: number }[];
  packageConsumedServiceIds?: number[];
}

const complete = async (id: number, body: ICompleteAppointment): Promise<IAppointmentView> => {
  try {
    const { data } = await Api.post(`/appointment/${id}/complete`, body);
    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface IUpdateAppointment {
  id: number;
  date: Date;
  canceled: boolean;
  userId?: number;
  services: IAppointmentServiceDto[];
  products: IAppointmentProductDto[];
  businessEmployeeId: number;
  businessId: number;
  paymentMethod?: number;
  paymentSplits?: { paymentMethod: number; amount: number }[];
  notes?: string;
  cancelReason?: string;
}

const update = async (body: IUpdateAppointment): Promise<IAppointmentView> => {
  try {
    const { data } = await Api.put(
      '/appointment',
      body
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const remove = async (id: number): Promise<void> => {
  try {
    await Api.delete(
      `/appointment/${id}`
    );

  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const updateStatus = async (appointmentId: number, status: number): Promise<void> => {
  try {
    await Api.put('/appointment/status', { appointmentId, status });
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export const appointmentService = {
  getById,
  get,
  create,
  complete,
  update,
  remove,
  updateStatus,
};
